import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Wrench, User, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

const CardinalProject = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const problemPoints = [
    { title: "Missed Payments", desc: "Each card quietly demands payment on the right day — miss it and you pay interest and bruise your credit\u00A0score." },
    { title: "Lost Rewards", desc: "Every card has optimal spending categories — use the wrong one and you leave rewards on the table at every\u00A0checkout." },
    { title: "Fragmented Visibility", desc: "Banks won't solve this — each bank's app only cares about its own card. Nobody owns the full\u00A0wallet." },
  ];

  const competitiveScan = [
    { player: "MaxRewards", gap: "Strong rewards engine — US-only, login-based.", icon: "/images/cardinal/maxrewards.png" },
    { player: "CardPointers", gap: "Optimizer-brain density; light on the due-date\u00A0moment.", icon: "/images/cardinal/cardpointers.png" },
    { player: "CardStack (CA)", gap: "Canadian, but manual-tracking UX; no wallet-level\u00A0advice.", icon: "/images/cardinal/cardstack.png" },
    { player: "creditcardGenius", gap: "Comparison-site energy — shopping for cards, not living with\u00A0them.", icon: "/images/cardinal/creditcardgenius.png" },
    { player: "Bank apps", gap: "One card each; structurally can't see the whole\u00A0wallet." },
  ];

  const strategyDecisions = [
    { number: "01", title: "Due date is the only required field", desc: "Adding a card = pick it from a Canadian catalogue, tap a day of the month. No numbers, no statement dates, no\u00A0logins." },
    { number: "02", title: "Local-only v1", desc: "Everything lives on-device. \"Everything stays on this phone\" is written into the first screen — privacy as copy, not policy\u00A0PDF." },
    { number: "03", title: "The honest handoff", desc: "\"Open bank app\" deep-links to the bank's front door — Canadian banks expose nothing deeper. The sheet says so in plain\u00A0words." },
    { number: "04", title: "Advice only where it's true", desc: "The cheat-sheet is derived, never stored: a category appears only when one of your cards genuinely beats your best base\u00A0rate." },
    { number: "05", title: "Mark-as-paid is a feature", desc: "Confirming payment advances the cycle, silences urgency, and earns a small celebration — the core loop gets an emotional\u00A0payoff." },
    { number: "06", title: "No balances, by design", desc: "Without account data the app can't nag about debt — only help with timing and choice. That constraint became the\u00A0tone." },
  ];

  const dataModel = [
    { entity: "category", holds: "Fixed 10-item taxonomy (groceries, dining, gas, transit, travel, recurring bills, drugstore, FX, warehouse, everything-else)." },
    { entity: "card", holds: "Catalogue: issuer, name, network, fee, base rate, reward unit, APR." },
    { entity: "reward_rule", holds: "Card \u00D7 category earn rate, caps &\u00A0notes." },
    { entity: "user_card", holds: "The only user data: card ref, due day, optional nickname, paid-cycle." },
    { entity: "user", holds: "Reminder lead-days, theme." },
  ];

  const designExplorations = [
    { version: "v1", name: "Warm paper", desc: "Spruce & cream, civic-trust typography. Calm, but\u00A0anonymous.", image: "/images/cardinal/cardinal_4.webp" },
    { version: "v2", name: "Banknote craft", desc: "Generative guilloché engraving, editorial serif\u00A0numerals.", image: "/images/cardinal/cardinal_5.webp" },
    { version: "v3", name: "Midnight vault", desc: "Aurora over near-black; the month as an orbital gold\u00A0dial.", image: "/images/cardinal/cardinal_6.webp" },
    { version: "v4", name: "Final", desc: "Charcoal luxe from the moodboard: Playfair \u00D7 Inter, vivid card\u00A0light.", image: "/images/cardinal/cardinal_7.webp" },
  ];

  const wcagAudit = [
    { element: "Muted text · dark theme", before: "3.27", after: "5.67" },
    { element: "Muted text · light theme", before: "2.84", after: "4.46" },
    { element: "White on accent button", before: "2.48", after: "4.93" },
    { element: "White on \"Due\" badge (hot)", before: "2.48", after: "4.93" },
    { element: "White on \"Paid ✓\" badge", before: "4.04", after: "5.32" },
    { element: "Rate chips · light theme", before: "2.12", after: "5.96" },
    { element: "Badge scrim over bright card art", before: "5.22", after: "7.18" },
    { element: "Gold \"Best value\" ink on gradient", before: "—", after: "5.04–10.12" },
  ];

  const accessibilityFeatures = [
    "Targets ≥ 44px — nav, pay actions, day picker, pills, segments; all\u00A0measured.",
    "Keyboard — visible focus ring on every\u00A0control.",
    "Motion — prefers-reduced-motion silences confetti, sheens, and\u00A0springs.",
    "Screen readers — toast is a polite live region; icon buttons labelled; every gesture has a tap equivalent\u00A0(WCAG 2.5.1).",
    "Themes — respects the system color scheme on first\u00A0launch.",
  ];

  const outcomeStats = [
    { value: "4", label: "complete design languages built & tested\u00A0interactively" },
    { value: "8", label: "WCAG issues found by measurement, fixed by\u00A0tokens" },
    { value: "0", label: "credentials, balances or permissions asked of the\u00A0user" },
  ];

  const learnings = [
    { title: "Honesty is a feature", desc: "Saying \"this button only opens your bank's front door\" out loud made every other promise\u00A0credible." },
    { title: "Constraints write the brand", desc: "No-credentials wasn't a limitation to hide — it became the first screen's\u00A0headline." },
    { title: "Kill your favourites", desc: "The guilloché engine and the orbital dial were the cleverest work here; the product didn't need\u00A0them." },
    { title: "Audit with math", desc: "Eyeballed contrast lies; computed ratios found failures in colors that \"looked\u00A0fine.\"" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed top-6 left-6 z-50"
      >
        <Button
          onClick={() => navigate("/#projects")}
          variant="outline"
          className="no-shiny h-12 px-4 rounded-full border-white/10 bg-black/50 backdrop-blur-xl text-white hover:bg-white/10"
          data-testid="cardinal-back-btn"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
      </motion.div>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-6 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
              FinTech
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] text-white mb-6">
              Cardinal
            </h1>
            <p className="text-xl md:text-2xl text-white/60 max-w-3xl mb-10">
              A credit-card companion for Canadians — never miss a payment, always tap the right&nbsp;card.
            </p>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-6 mb-12">
              <div className="flex items-center gap-2 text-white/50">
                <User className="w-5 h-5" />
                <span>End-to-end Product Design</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Calendar className="w-5 h-5" />
                <span>Personal Project · Hi-fi Prototype</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Wrench className="w-5 h-5" />
                <span>Research → System → UI →&nbsp;A11y</span>
              </div>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex justify-center"
          >
            <div className="rounded-3xl overflow-hidden border border-white/5 max-w-sm">
              <img
                src="/images/cardinal/cardinal_1.webp"
                alt="Cardinal Home Screen"
                className="w-full"
              />
            </div>
          </motion.div>
        </div>
      </section>


      {/* Preface */}
      <section className="py-20 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Preface
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white leading-tight mb-10">
              Where This Started
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="space-y-6 text-lg text-white/60 leading-relaxed">
                <p>
                  I spent quite some time as a retail cashier, and the card moment was when I watched people quietly lose. Some customers juggled three or four cards and had lost the thread — a forgotten statement, a payment that slipped past them. Others tapped the wrong card, paying for groceries on one that earned almost nothing while their 4% grocery card sat two slots deeper in the same&nbsp;wallet.
                </p>
                <p>
                  Neither is a discipline problem. It's a design problem: the two things people need — when is each card due, and which card wins here — live scattered across bank apps, statements, and memory, none of them present at the moment of&nbsp;decision.
                </p>
                <p className="text-white/80 font-medium">
                  Cardinal is my answer to that period of observation at the register. This case study is the story of designing&nbsp;it.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
            >
              <div className="rounded-2xl overflow-hidden border border-white/5">
                <img
                  src="/images/cardinal/preface-cashier.png"
                  alt="Customer choosing between multiple credit cards at checkout"
                  className="w-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              The Problem
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white leading-tight max-w-4xl">
              Two small failures, every single&nbsp;month
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed mt-6">
              Most Canadians carry more than one credit card, and each card quietly demands two things: pay me on the right day, and use me in the right store. Banks won't solve this — each bank's app only cares about its own&nbsp;card.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {problemPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{point.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{point.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What Cardinal Is */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
                What Cardinal Is
              </p>
              <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
                Two Jobs, Done Well
              </h2>
              <div className="space-y-6">
                <div className="p-6 rounded-2xl bg-white/5 border border-white/5">
                  <h3 className="text-lg font-bold text-white mb-2">Guardian</h3>
                  <p className="text-white/50 text-sm leading-relaxed">One gentle reminder before every due date, and a one-tap path to your bank's&nbsp;app.</p>
                </div>
                <div className="p-6 rounded-2xl bg-white/5 border border-white/5">
                  <h3 className="text-lg font-bold text-white mb-2">Coach</h3>
                  <p className="text-white/50 text-sm leading-relaxed">For every spending category, which of your cards earns the&nbsp;most.</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <p className="text-sm font-mono text-white/40 tracking-wider uppercase mb-4">
                What It Deliberately Is Not
              </p>
              <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
                Scope Discipline
              </h2>
              <p className="text-lg text-white/60 leading-relaxed mb-6">
                No bank logins. No balances. No payments handled in-app. Scope discipline became the product's trust&nbsp;story.
              </p>
              <p className="text-base text-white/40 leading-relaxed italic mb-8">
                The design bar: intuitive enough for a non-technical 70-year-old. Simplicity is the&nbsp;differentiator.
              </p>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/5">
                <p className="text-white/50 text-sm leading-relaxed">No account numbers. No statement dates. No linked bank credentials. Everything stays on-device — privacy as copy, not policy&nbsp;PDF.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Research */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Research
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
              Why the Obvious Playbooks Don't Transplant to&nbsp;Canada
            </h2>
          </motion.div>

          {/* CRED Teardown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-white/5 border border-white/5 mb-8"
          >
            <h3 className="text-xl font-bold font-['Syne'] text-white mb-4">
              Teardown — CRED (India)
            </h3>
            <p className="text-white/60 leading-relaxed mb-4">
              CRED made bill-paying aspirational: pay through the app, earn rewards. But its engine depends on UPI payment rails (instant, free, universal) and a curated affluent user base. Canada has no UPI equivalent — and card payment initiation isn't open to third parties&nbsp;yet.
            </p>
            <p className="text-red-400/80 text-sm font-medium">
              Implication: "Pay inside the app" is structurally impossible here today. Any honest v1 must hand off to the&nbsp;bank.
            </p>
          </motion.div>

          {/* Open Banking */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-white/5 border border-white/5 mb-8"
          >
            <h3 className="text-xl font-bold font-['Syne'] text-white mb-4">
              Open Banking Timeline
            </h3>
            <div className="space-y-3 text-white/60">
              <p><span className="text-white font-medium">Bill C-15</span> — consumer-driven banking framework, royal assent March&nbsp;2026.</p>
              <p><span className="text-white font-medium">Phase 1</span> — read access (~early 2026); <span className="text-white font-medium">Phase 2</span> — payment initiation (~mid-2027).</p>
              <p className="text-red-400/80 text-sm font-medium mt-4">Implication: Design for the pre-open-banking world now; architect so account data can slot in later without changing the mental&nbsp;model.</p>
            </div>
          </motion.div>

          {/* Competitive Scan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold font-['Syne'] text-white mb-6">
              Competitive Scan
            </h3>
            <div className="overflow-hidden rounded-2xl border border-white/5">
              <table className="w-full">
                <thead>
                  <tr className="bg-white/5">
                    <th className="text-left p-4 text-sm font-medium text-white/80">Player</th>
                    <th className="text-left p-4 text-sm font-medium text-white/80">Gap for This Job</th>
                  </tr>
                </thead>
                <tbody>
                  {competitiveScan.map((item, index) => (
                    <tr key={item.player} className="border-t border-white/5">
                      <td className="p-4 text-sm text-white font-medium">
                        <div className="flex items-center gap-3">
                          {item.icon && (
                            <img src={item.icon} alt={item.player} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                          )}
                          {item.player}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-white/50">{item.gap}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-red-400 mt-6 leading-relaxed font-medium">
              The open seat: a Canada-first app that owns the due-date moment and answers "which card?" for your wallet — with zero&nbsp;credentials.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Strategy */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Strategy
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-4">
              Reminders Are the Wedge. Advice Is the&nbsp;Retention.
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              Every decision below traded feature surface for trust and repeat use. The reminder is why you install Cardinal; the cheat-sheet you absorb on every open is why you keep&nbsp;it.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {strategyDecisions.map((decision, index) => (
              <motion.div
                key={decision.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <span className="text-3xl font-bold text-white/10 font-['Syne']">{decision.number}</span>
                <h3 className="text-lg font-bold text-white mt-2 mb-3">{decision.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{decision.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Repetition builds recall */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 pl-6 border-l-2 border-red-500/40"
          >
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed font-['Syne']">
              Repetition builds recall.
            </p>
            <p className="text-base text-white/50 leading-relaxed mt-4 max-w-4xl">
              Every time someone opens Cardinal to check a due date, the same rewards table is right there — same categories, same order, every time. Over weeks of these quick glances, the pairing between category and card starts to stick naturally. The app is still the source of truth, but a side benefit emerges: reaching for the right card at checkout gets a little more automatic each&nbsp;month.
            </p>
          </motion.div>
        </div>
      </section>
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              System Design
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-4">
              A Tiny, Honest Data Model
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              Five tables, nothing more. The cheat-sheet is computed at read time — no cached advice to go&nbsp;stale.
            </p>
          </motion.div>

          <div className="overflow-hidden rounded-2xl border border-white/5 mb-12">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left p-4 text-sm font-medium text-white/80">Entity</th>
                  <th className="text-left p-4 text-sm font-medium text-white/80">Holds</th>
                </tr>
              </thead>
              <tbody>
                {dataModel.map((item, index) => (
                  <tr key={item.entity} className="border-t border-white/5">
                    <td className="p-4 text-sm text-red-400 font-mono">{item.entity}</td>
                    <td className="p-4 text-sm text-white/50">{item.holds}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-white/5 border border-white/5"
            >
              <h3 className="text-lg font-bold text-white mb-3">Normalization</h3>
              <p className="text-white/50 text-sm leading-relaxed">Every rate converts to cents-per-dollar via a per-program value map. So Cobalt's 5x MR (5.0¢) legitimately beats Momentum's 4% (4.0¢) on&nbsp;groceries.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6 rounded-2xl bg-white/5 border border-white/5"
            >
              <h3 className="text-lg font-bold text-white mb-3">Deterministic Tie-breaks</h3>
              <p className="text-white/50 text-sm leading-relaxed">Same value? Highest normalized rate → lower annual fee → issuer A–Z. Decided, never&nbsp;random.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-6 rounded-2xl bg-white/5 border border-white/5"
            >
              <h3 className="text-lg font-bold text-white mb-3">Dates That Don't Lie</h3>
              <p className="text-white/50 text-sm leading-relaxed">Due-day 31 clamps to each month's real end (Jan 31 → Feb 28); a paid cycle rolls the next due date&nbsp;forward.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lo-fi Wireframes */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Low-Fidelity Wireframes
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-4">
              Before Pixels: Two States on&nbsp;Paper
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              The whole product was first argued out in ink. Two sketches of the same home screen — calm and urgent — fixed the app's emotional range before any visual design&nbsp;existed.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl overflow-hidden border border-white/5 mb-6">
                <img src="/images/cardinal/cardinal_2.webp" alt="Wireframe — nothing due soon" className="w-full" />
              </div>
              <h3 className="text-xl font-bold font-['Syne'] text-white mb-3">State 1 — Nothing Due Soon</h3>
              <p className="text-white/50 text-sm leading-relaxed">Card stack as the hero object, perks + due date as two columns of truth. "Theme changes as due date gets closer (green → orange → red)" — the seed of the final ambient&nbsp;system.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="rounded-2xl overflow-hidden border border-white/5 mb-6">
                <img src="/images/cardinal/cardinal_3.webp" alt="Wireframe — due date close" className="w-full" />
              </div>
              <h3 className="text-xl font-bold font-['Syne'] text-white mb-3">State 2 — Due Date Is Close</h3>
              <p className="text-white/50 text-sm leading-relaxed">"Area turns red gradient" — urgency as atmosphere, not a notification badge. The flash became a considered celebration: urgency shouldn't strobe — resolution should&nbsp;reward.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Design Exploration */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Design Exploration
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-4">
              Four Directions, One Survivor
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              From those sketches, four fully-interactive design languages were built on the same working logic — then pressure-tested against a curated moodboard and the 70-year-old&nbsp;bar.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {designExplorations.map((item, index) => (
              <motion.div
                key={item.version}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`rounded-2xl overflow-hidden border ${index === 3 ? 'border-red-500/30 ring-1 ring-red-500/20' : 'border-white/5'}`}
              >
                <img src={item.image} alt={`Cardinal ${item.version}`} className="w-full" />
                <div className="p-4 bg-[#121212]">
                  <p className="text-white font-bold text-sm">{item.version} · {item.name}</p>
                  <p className="text-white/40 text-xs mt-1">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 p-6 rounded-2xl bg-red-500/5 border border-red-500/10"
          >
            <p className="text-white/60 leading-relaxed">
              <span className="text-white font-medium">Why v4 won:</span> the moodboard pointed at the premium register of modern card culture — recognizable card art as the hero object, editorial serif for moments of worth, one crimson accent reserved for urgency. v2's guilloché and v3's dial were the better inventions; v4 was the better product for the&nbsp;audience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Interface */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              The Interface
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-4">
              Every Due Date Visible. Zero Taps&nbsp;Required.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12 max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl overflow-hidden border border-white/5">
                <img src="/images/cardinal/cardinal_8.webp" alt="Home, dark theme" className="w-full" />
              </div>
              <p className="text-white/40 text-sm mt-4 text-center">Dark — a payment inside the 7-day&nbsp;window.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="rounded-2xl overflow-hidden border border-white/5">
                <img src="/images/cardinal/cardinal_9.webp" alt="Home, light theme" className="w-full" />
              </div>
              <p className="text-white/40 text-sm mt-4 text-center">Light — the same wallet on warm&nbsp;paper.</p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              "Vertical card stack — recognizable card art, capped at three; a \"Show all\" sheet handles bigger\u00A0wallets.",
              "Due dates live on card edges — every visible strip carries its own badge; urgency turns\u00A0crimson.",
              "Drag-follow swipe — cards track the finger with a subtle tilt, spring back on\u00A0hesitation.",
              "Actions only when they matter — \"I've paid it\" and \"Open bank app\" appear solely inside the 7-day\u00A0window.",
            ].map((feature, index) => (
              <motion.div
                key={`interface-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="p-5 rounded-2xl bg-white/5 border border-white/5"
              >
                <p className="text-white/50 text-sm leading-relaxed">{feature}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Flows */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Key Flows
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-4">
              Thirty Seconds to Onboard. A Tiny Celebration to&nbsp;Retain.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-10 mb-16 max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl overflow-hidden border border-white/5">
                <img src="/images/cardinal/cardinal_10.webp" alt="Add a card — due day picker" className="w-full" />
              </div>
              <p className="text-white/40 text-sm mt-4 text-center">Add a card, step 2 of 2 — "That's all we need. No account numbers,&nbsp;ever."</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="rounded-2xl overflow-hidden border border-white/5">
                <img src="/images/cardinal/cardinal_11.webp" alt="Settings — reminders and appearance" className="w-full" />
              </div>
              <p className="text-white/40 text-sm mt-4 text-center">Settings — reminder lead-time, dark/light appearance, the privacy&nbsp;promise.</p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-white/5 border border-white/5"
            >
              <h3 className="text-lg font-bold text-white mb-3">Add a Card</h3>
              <p className="text-white/50 text-sm leading-relaxed">Search a curated Canadian catalogue → tap the day it's due. A reminder is scheduled instantly, confirmed in plain&nbsp;language.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6 rounded-2xl bg-white/5 border border-white/5"
            >
              <h3 className="text-lg font-bold text-white mb-3">Mark as Paid</h3>
              <p className="text-white/50 text-sm leading-relaxed">Confetti bursts, the badge flips to "Paid ✓", the cycle rolls forward, and a toast signs off: "Paid. See you next&nbsp;month."</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-6 rounded-2xl bg-white/5 border border-white/5"
            >
              <h3 className="text-lg font-bold text-white mb-3">The Honest Handoff</h3>
              <p className="text-white/50 text-sm leading-relaxed">A sheet tells the truth: the real app launches your bank's own app, where you log in and pay as usual — Cardinal never touches the&nbsp;money.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Rewards Intelligence */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Rewards Intelligence
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-4">
              Every Perk Shown. The Winners Marked in&nbsp;Gold.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-10 items-center">
            <div className="md:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl overflow-hidden border border-white/5 max-w-xs mx-auto"
              >
                <img src="/images/cardinal/cardinal_12.webp" alt="Card detail — rewards table" className="w-full" />
              </motion.div>
            </div>
            <div className="md:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-white/60 leading-relaxed mb-6">
                  Each card page lays out the facts a cardholder actually weighs: payment due, annual fee, and purchase interest — the APR sitting one row under the due date quietly argues for paying on&nbsp;time.
                </p>
                <div className="p-5 rounded-2xl bg-white/5 border border-white/5">
                  <h3 className="text-lg font-bold text-white mb-3">Best Value — The Gold System</h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    Where this card genuinely beats every other card in your wallet, the row earns a metallic-gold tag — champagne-to-brass gradient, bronze ink. Gold rhymes with the chip on every card; crimson stays reserved for&nbsp;urgency.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Accessibility */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Accessibility
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-4">
              WCAG Audit — Measured, Not&nbsp;Eyeballed
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              Every text/background pair was computed programmatically against WCAG 2.x. Seven failures surfaced and were fixed by adjusting tokens — the design barely moved, the math now&nbsp;passes.
            </p>
          </motion.div>

          <div className="overflow-hidden rounded-2xl border border-white/5 mb-12">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left p-4 text-sm font-medium text-white/80">Element</th>
                  <th className="text-center p-4 text-sm font-medium text-white/80">Before</th>
                  <th className="text-center p-4 text-sm font-medium text-white/80">After</th>
                </tr>
              </thead>
              <tbody>
                {wcagAudit.map((item, index) => (
                  <tr key={item.element} className="border-t border-white/5">
                    <td className="p-4 text-sm text-white/60">{item.element}</td>
                    <td className="p-4 text-sm text-center text-red-400/60">{item.before}</td>
                    <td className="p-4 text-sm text-center text-emerald-400">{item.after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accessibilityFeatures.map((feature, index) => (
              <motion.div
                key={`a11y-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="p-5 rounded-2xl bg-white/5 border border-white/5"
              >
                <p className="text-white/50 text-sm leading-relaxed">{feature}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcome */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Outcome
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-4">
              A Living Prototype, Not a Picture of&nbsp;One
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              The deliverable is a fully interactive, single-file prototype where the real logic runs live — reward normalization, best-card derivation with tie-breaks, the due-date engine with month-end clamping, paid cycles, dual&nbsp;themes.
            </p>
          </motion.div>

          <div className="grid grid-cols-3 gap-6 mb-16">
            {outcomeStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5 text-center"
              >
                <p className="text-4xl md:text-5xl font-bold font-['Syne'] text-white mb-3">{stat.value}</p>
                <p className="text-white/50 text-sm leading-relaxed">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Prototype */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Try It Yourself
            </p>
            <h3 className="text-2xl md:text-3xl font-bold font-['Syne'] text-white mb-6">
              Interactive Prototype
            </h3>
            <p className="text-base text-white/50 max-w-3xl">
              Add cards, mark payments, switch themes, and watch the rewards cheat-sheet recompute live. Everything runs in your&nbsp;browser.
            </p>
          </motion.div>

          <div className="relative flex justify-center" style={{ height: '900px' }}>
            {/* Deep drop shadow */}
            <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
              <div className="w-[430px] h-[860px] rounded-[64px]" style={{
                boxShadow: '0 60px 120px -10px rgba(0,0,0,0.7), 0 30px 60px -10px rgba(0,0,0,0.5)',
              }} />
            </div>
            {/* Ambient screen glow */}
            <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
              <div className="w-[480px] h-[900px] rounded-[90px]" style={{
                background: 'radial-gradient(ellipse at 50% 30%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 35%, transparent 65%)',
              }} />
            </div>
            {/* Bezel edge highlight */}
            <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
              <div className="w-[440px] h-[865px] rounded-[68px]" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.06) 15%, transparent 35%, transparent 75%, rgba(255,255,255,0.03) 90%, rgba(255,255,255,0.08) 100%)',
              }} />
            </div>
            <iframe
              src="/cardinal-prototype.html"
              title="Cardinal Interactive Prototype"
              className="h-full border-0 relative z-10"
              style={{ width: '500px' }}
              allow="fullscreen"
              data-testid="cardinal-prototype-iframe"
            />
          </div>
        </div>
      </section>

      {/* Learnings */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Reflections
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              What This Project Taught&nbsp;Me
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {learnings.map((learning, index) => (
              <motion.div
                key={learning.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{learning.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{learning.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-b from-[#0d0d0d] to-[#0A0A0A]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-red-400 tracking-wider uppercase mb-4">
              Conclusion
            </p>
            <h2 className="text-2xl md:text-3xl font-bold font-['Syne'] text-white mb-6 leading-relaxed">
              Cardinal proves that a single constraint — zero credentials — can write an entire product's brand, trust story, and emotional tone. By trading feature surface for honesty, the app earns the right to one daily&nbsp;question: "which card, and when is it&nbsp;due?"
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Button
              onClick={() => navigate("/#projects")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
              data-testid="cardinal-back-to-portfolio-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Portfolio
            </Button>
            
            <Button
              onClick={() => navigate("/#contact")}
              className="h-12 px-8 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-all"
              data-testid="cardinal-contact-btn"
            >
              Get in Touch
            </Button>
            
            <Button
              onClick={() => navigate("/projects/fintell")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
              data-testid="cardinal-next-project-btn"
            >
              Next: FinTell
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CardinalProject;
